import { Link } from "react-router-dom";
import type { EventItem } from "@/mocks/events";

interface EventCardProps {
  event: EventItem;
  variant?: "grid" | "list" | "compact";
}

const badgeStyles: Record<string, string> = {
  爆: "bg-primary-500 text-background-50",
  热: "bg-accent-500 text-primary-950",
  议: "bg-secondary-500 text-background-50",
};

function Badge({ value }: { value: string }) {
  const style = badgeStyles[value] || "bg-secondary-500 text-background-50";
  return (
    <span
      className={`inline-flex h-6 items-center rounded-full px-2.5 text-xs font-bold tracking-wide ${style}`}
    >
      {value}
    </span>
  );
}

export default function EventCard({ event, variant = "grid" }: EventCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/event/${event.id}`}
        className="group flex items-start gap-3 border-b border-background-200 py-3 last:border-b-0"
      >
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-xs font-bold text-primary-600">
          {event.year.slice(2)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-heading text-sm font-bold leading-snug text-foreground-900 transition-colors line-clamp-2 group-hover:text-primary-600">
            {event.title}
          </span>
          <span className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
            <span>{event.categoryName}</span>
            <span>·</span>
            <span>{event.views} 阅读</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "list") {
    return (
      <article className="group overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-300">
        <Link to={`/event/${event.id}`} className="flex flex-col sm:flex-row">
          <div className="relative h-48 w-full shrink-0 overflow-hidden bg-secondary-100 sm:h-auto sm:w-[200px] md:w-[280px]">
            <img
              src={event.cover}
              alt={`${event.title} - 爆料专区年度盘点`}
              title={`${event.title} - 爆料专区事件档案`}
              className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <span className="absolute left-3 top-3">
              <Badge value={event.badge} />
            </span>
          </div>
          <div className="flex min-w-0 flex-1 flex-col p-4 md:p-6">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
              <span className="font-medium text-primary-600">{event.categoryName}</span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {event.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" />
                {event.views}
              </span>
            </div>
            <h3 className="mt-2.5 font-heading text-lg font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600">
              {event.title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2 md:line-clamp-3">
              {event.summary}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {event.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-600 ring-1 ring-background-200"
                >
                  {tag}
                </span>
              ))}
            </div>
            <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-information-line" />
                {event.status}
              </span>
              <span className="flex items-center gap-1 font-medium text-foreground-700 transition-colors group-hover:text-primary-600">
                查看完整档案
                <i className="ri-arrow-right-line" />
              </span>
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-300">
      <Link to={`/event/${event.id}`} className="flex h-full flex-col">
        <div className="relative h-44 w-full overflow-hidden bg-secondary-100 md:h-48">
          <img
            src={event.cover}
            alt={`${event.title} - 爆料专区年度盘点`}
            title={`${event.title} - 爆料专区事件档案`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute left-3 top-3">
            <Badge value={event.badge} />
          </span>
          <span className="absolute right-3 top-3 rounded-full bg-background-50/90 px-2.5 py-1 text-xs font-medium text-foreground-700">
            {event.year} · {event.categoryName}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-4 md:p-5">
          <h3 className="font-heading text-base font-bold leading-snug text-foreground-950 transition-colors line-clamp-2 group-hover:text-primary-600">
            {event.title}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2">
            {event.summary}
          </p>
          <div className="mt-auto flex items-center justify-between pt-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {event.views}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {event.readTime}阅读
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}